import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { USER_ROLES, roleDashboards, getAccessibleModules, canAccessModule } from '../config/moduleRegistry';
import axios from 'axios';
import logger from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardContext = createContext(null);

// Normalize role string from database format ("Club Admin") to code format ("club_admin")
const normalizeRole = (role) => {
  if (!role) return null;
  // Convert "Club Admin" -> "club_admin", "Coach" -> "coach", etc.
  return role.toLowerCase().replace(/\s+/g, '_');
};

export const DashboardProvider = ({ children }) => {
  const { user, profile } = useAuth();
  
  // User role state - defaults to 'fan' for logged in users
  const [userRole, setUserRole] = useState(USER_ROLES.FAN);
  
  // Organizations the user belongs to
  const [userOrganizations, setUserOrganizations] = useState([]);
  
  // Currently selected organization context
  const [currentOrganization, setCurrentOrganization] = useState(null);
  
  // Dashboard configuration for current role
  const [dashboardConfig, setDashboardConfig] = useState(roleDashboards[USER_ROLES.FAN]);
  
  // Enabled modules based on role + org
  const [enabledModules, setEnabledModules] = useState([]);
  
  // Widget preferences
  const [widgetPreferences, setWidgetPreferences] = useState([]);
  
  // Loading state
  const [loading, setLoading] = useState(true);

  // Fetch user's organizations and roles
  const fetchUserContext = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Get user's organization memberships
      const response = await axios.get(`${API}/users/${user.id}/organizations`);
      const orgs = response.data || [];
      setUserOrganizations(orgs);

      // Determine the user's primary role
      // Priority: Check profile role first, then org membership roles
      let determinedRole = USER_ROLES.FAN;
      
      if (profile?.role) {
        determinedRole = profile.role;
      } else if (orgs.length > 0) {
        // Use the highest privilege role from their memberships
        const roleHierarchy = [
          USER_ROLES.SUPER_ADMIN,
          USER_ROLES.PLATFORM_ADMIN,
          USER_ROLES.FEDERATION_ADMIN,
          USER_ROLES.CLUB_ADMIN,
          USER_ROLES.ACADEMY_ADMIN,
          USER_ROLES.CORPORATE_ADMIN,
          USER_ROLES.EVENT_MANAGER,
          USER_ROLES.FACILITY_MANAGER,
          USER_ROLES.COACH,
          USER_ROLES.ATHLETE,
          USER_ROLES.VENDOR,
          USER_ROLES.FAN
        ];
        
        for (const role of roleHierarchy) {
          if (orgs.some(org => normalizeRole(org.role) === role)) {
            determinedRole = role;
            break;
          }
        }
      }
      
      setUserRole(determinedRole);
      
      // Set first org as current if user has any and no current org selected
      if (orgs.length > 0) {
        setCurrentOrganization(prevOrg => prevOrg || orgs[0]);
      }
    } catch (err) {
      logger.error('Failed to fetch user context:', err);
      // Default to fan role if fetch fails
      setUserRole(USER_ROLES.FAN);
    } finally {
      setLoading(false);
    }
  }, [user, profile]);

  // Update dashboard config when role changes
  useEffect(() => {
    const config = roleDashboards[userRole] || roleDashboards[USER_ROLES.FAN];
    setDashboardConfig(config);
    
    const modules = getAccessibleModules(userRole, currentOrganization?.type);
    setEnabledModules(modules);
    
    // Set default widgets from role config
    setWidgetPreferences(config.defaultWidgets || []);
  }, [userRole, currentOrganization?.type]);

  // Initialize user context
  useEffect(() => {
    fetchUserContext();
  }, [fetchUserContext]);

  // Switch organization context
  const switchOrganization = useCallback((org) => {
    setCurrentOrganization(org);
    
    // Update role based on org membership
    const membership = userOrganizations.find(o => o.id === org.id);
    if (membership?.role) {
      setUserRole(normalizeRole(membership.role));
    }
  }, [userOrganizations]);

  // Check module access
  const hasModuleAccess = useCallback((moduleId) => {
    return canAccessModule(moduleId, userRole);
  }, [userRole]);

  // Temporarily switch role (for demo/testing)
  const switchRole = useCallback((newRole) => {
    if (Object.values(USER_ROLES).includes(newRole)) {
      setUserRole(newRole);
    }
  }, []);

  // Update widget preferences
  const updateWidgets = useCallback((widgets) => {
    setWidgetPreferences(widgets);
    // Optionally persist to backend
    // axios.put(`${API}/users/${user?.id}/widget-preferences`, { widgets });
  }, []);

  const value = useMemo(() => ({
    // User role info
    userRole,
    switchRole,
    
    // Organization context
    userOrganizations,
    currentOrganization,
    switchOrganization,
    
    // Dashboard config
    dashboardConfig,
    enabledModules,
    hasModuleAccess,
    
    // Widgets
    widgetPreferences,
    updateWidgets,
    
    // Loading state
    loading,
    
    // Refresh context
    refreshContext: fetchUserContext
  }), [
    userRole,
    switchRole,
    userOrganizations,
    currentOrganization,
    switchOrganization,
    dashboardConfig,
    enabledModules,
    hasModuleAccess,
    widgetPreferences,
    updateWidgets,
    loading,
    fetchUserContext
  ]);

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within DashboardProvider');
  }
  return context;
};

export default DashboardContext;
